import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  BankNote01,
  Check,
  ChevronLeft,
  Colors,
  DotsVertical,
  FilePlus02,
  Globe05,
  MessageTextSquare02,
  ShoppingBag01,
  Target04,
  User01,
  Users02,
  XCircle,
  XClose,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCandidate } from "@/api/candidates";
import {
  type TargetCollaborationCreator,
  useApproveSampleRequest,
  useDenySampleRequest,
  useOpenCollaborationCreators,
  useOpenCollaborationDetail,
  useTargetCollaborationCreators,
  useTargetCollaborationDetail,
} from "@/api/collaborations";
import { useCreator } from "@/api/creator";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { VideoDetails } from "@/components/video-details";
import { useCurrency } from "@/hooks/use-currency";
import { AddToCandidateListsModal } from "@/pages/candidate-lists/add-to-candidate-lists-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { CandidateDetails } from "@/pages/creators/candidate-details";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { useMerchant } from "@/providers/MerchantProvider";
import { success } from "@/providers/ToastProvider";
import { candidateExplorerRoute, creatorExplorerRoute } from "@/router";
import { cx } from "@/utils/cx";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { CandidateClassificationCard } from "../creators/candidate-classification-card";
import { ClassificationCard } from "../creators/classification-card";
import { SampleProductThumbnails } from "./sample-product-thumbnails";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numFormatter = new Intl.NumberFormat("en-US", { notation: "compact", compactDisplay: "short" });
const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const BATCH_SIZE = 400;

type SortColumn = "name" | "score";
type SortDirection = "asc" | "desc";

/**
 * Unified creator shape used internally by SampleRequestsView for both
 * target and open collaborations. Fields present only in one type are optional.
 */
type NormalizedSampleRequestCreator = {
  tiktokCreatorOpenId: string;
  creatorId: string | null;
  isCreator: boolean;
  username?: string;
  nickname: string | null;
  avatar: string | null;
  status: "active" | "removed";
  avgGmvPerBuyerAmount: number | null;
  avgGmvPerBuyerCurrency: string | null;
  followerCount: number | null;
  sampleProducts: { skuImageUrl: string | null; skuName: string | null }[];
  pendingSampleApplicationId: string | null;
  matchQualityScore?: number | null;
  collaborationCount?: number | null;
  collaborationNames?: string[] | null;
};

const SortIcon = ({ active, direction }: { active: boolean; direction: SortDirection }) => (
  <svg
    aria-hidden="true"
    className={cx(
      "w-3.5 h-3.5 transition-transform",
      active ? "text-fg-secondary" : "text-fg-quaternary",
      direction === "asc" && active ? "rotate-180" : "",
    )}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 2.333v9.334M7 11.667L3.5 8.167M7 11.667l3.5-3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 text-sm font-medium text-secondary pb-2 border-b border-border-secondary">
    {children}
  </div>
);

// ─── Profile right column ─────────────────────────────────────────────────────

const ProfileRightColumn = ({
  topCategories,
  demographicsGender,
  demographicsAge,
  followersLabel,
  avgCommissionRateLabel,
  profilePage,
  currencyFormatter,
  candidateClassification,
  ecVideoCount,
  gmvAmount,
  creatorClassification,
  avgVideos,
  avgGMV,
  videosWithTier1,
  percentageTier2AtRisk,
}: {
  topCategories: string[];
  demographicsGender: { label: string; value: string } | null;
  demographicsAge: { label: string; value: string } | null;
  followersLabel: string;
  avgCommissionRateLabel: string;
  profilePage?: string | null;
  currencyFormatter: Intl.NumberFormat;
  candidateClassification?: string | null;
  ecVideoCount?: number;
  gmvAmount?: number;
  creatorClassification?: string | null;
  avgVideos?: number;
  avgGMV?: number;
  videosWithTier1?: number;
  percentageTier2AtRisk?: number;
}) => {
  const merchant = useMerchant();
  const displayCategories = topCategories.slice(0, 3);
  const categoryCount = Math.min(topCategories.length, 3);

  return (
    <div className="w-[360px] flex-shrink-0 p-8 pt-16 flex flex-col gap-14">
      {candidateClassification && ecVideoCount != null && gmvAmount != null && (
        <CandidateClassificationCard
          classification={
            candidateClassification as Parameters<typeof CandidateClassificationCard>[0]["classification"]
          }
          topCategory={displayCategories[0]}
          ecVideoCount={ecVideoCount}
          gmvAmount={gmvAmount}
          currencyFormatter={currencyFormatter}
          compact
        />
      )}

      {creatorClassification &&
        avgVideos != null &&
        avgGMV != null &&
        videosWithTier1 != null &&
        percentageTier2AtRisk != null && (
          <ClassificationCard
            classification={creatorClassification as Parameters<typeof ClassificationCard>[0]["classification"]}
            topCategory={displayCategories[0]}
            avgVideos={avgVideos}
            avgGMV={avgGMV}
            videosWithTier1={videosWithTier1}
            percentageTier2AtRisk={percentageTier2AtRisk}
            currencyFormatter={currencyFormatter}
            compact
          />
        )}

      {displayCategories.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionHeader>
            <Colors className="w-4 h-4" />
            Top {categoryCount} selling {categoryCount === 1 ? "category" : "categories"}
          </SectionHeader>
          <div className="flex flex-wrap gap-2">
            {displayCategories.map((cat) => {
              const isCandidate = !!candidateClassification;
              return merchant ? (
                <Link
                  key={cat}
                  to={isCandidate ? candidateExplorerRoute.to : creatorExplorerRoute.to}
                  params={{ merchantId: merchant }}
                  search={
                    isCandidate
                      ? { topSellingCategories: [{ id: cat, label: cat }] }
                      : { topSellingCategories: [{ id: cat, label: cat }], classification: "__all" }
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-fit"
                  aria-label={`Browse ${isCandidate ? "candidates" : "creators"} in ${cat}`}
                >
                  <Badge color="gray" className="cursor-pointer hover:ring-2 hover:ring-brand-primary transition-all">
                    {cat}
                  </Badge>
                </Link>
              ) : (
                <Badge key={cat} color="gray">
                  {cat}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {(demographicsGender || demographicsAge) && (
        <div className="flex flex-col gap-3">
          <SectionHeader>
            <Globe05 className="w-4 h-4" />
            Demographics
          </SectionHeader>
          <div className="flex flex-col">
            {demographicsGender && (
              <div className="flex justify-between py-2 border-b border-border-secondary">
                <span className="text-sm text-secondary capitalize">{demographicsGender.label}</span>
                <span className="text-sm text-secondary">{demographicsGender.value}</span>
              </div>
            )}
            {demographicsAge && (
              <div className="flex justify-between py-2 border-b border-border-secondary">
                <span className="text-sm text-secondary">Age {demographicsAge.label}</span>
                <span className="text-sm text-secondary">{demographicsAge.value}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 py-1 border-b border-border-secondary">
          <Users02 className="w-4 h-4" />
          <span className="text-base font-medium text-primary">{followersLabel}</span>
          <span className="text-sm text-tertiary">Followers</span>
        </div>
        <div className="flex items-center gap-2 py-1 border-b border-border-secondary">
          <BankNote01 className="w-4 h-4" />
          <span className="text-base font-medium text-primary">{avgCommissionRateLabel}</span>
          <span className="text-sm text-tertiary">Average commission rate</span>
        </div>
        {profilePage && (
          <div className="pt-2">
            <a
              href={profilePage}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-secondary hover:text-primary hover:underline flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" role="img" aria-label="TikTok">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
              </svg>
              See TikTok profile <span className="text-xs">↗</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Video grid ───────────────────────────────────────────────────────────────

const VideoGrid = ({
  videos,
  onVideoClick,
}: {
  videos: {
    id?: string;
    thumbnail?: string | null;
    label1: string;
    value1: string;
    label2: string;
    value2: string;
  }[];
  onVideoClick?: (videoId: string) => void;
}) => (
  <div className="grid grid-cols-3 gap-4">
    {videos.map((v) => (
      <div key={v.id ?? v.thumbnail} className="flex flex-col gap-2">
        {onVideoClick && v.id ? (
          <button
            type="button"
            onClick={() => onVideoClick(v.id ?? "")}
            className="block rounded-lg overflow-hidden bg-bg-quaternary aspect-[9/13] relative group cursor-pointer"
          >
            {v.thumbnail ? (
              <img
                src={v.thumbnail}
                alt=""
                className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
              />
            ) : (
              <div className="w-full h-full bg-bg-quaternary" />
            )}
          </button>
        ) : (
          <div className="block rounded-lg overflow-hidden bg-bg-quaternary aspect-[9/13] relative">
            {v.thumbnail ? (
              <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-bg-quaternary" />
            )}
          </div>
        )}
        <div className="flex justify-between text-xs text-quaternary">
          <div>
            <div>{v.label1}</div>
            <div className="font-medium">{v.value1}</div>
          </div>
          <div className="text-right">
            <div>{v.label2}</div>
            <div className="font-medium">{v.value2}</div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ─── Profile header ───────────────────────────────────────────────────────────

const ProfileHeader = ({
  creator,
  categorySubtitle,
  description,
  onOpenProfile,
}: {
  creator: NormalizedSampleRequestCreator;
  categorySubtitle: string;
  description?: string | null;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
}) => {
  const profileId = creator.creatorId ?? creator.tiktokCreatorOpenId;
  const isCreator = !!creator.creatorId;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-start">
        {creator.avatar ? (
          <img
            src={creator.avatar}
            alt={creator.nickname ?? creator.username ?? ""}
            className="w-20 h-20 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-bg-quaternary flex items-center justify-center flex-shrink-0">
            <User01 className="w-8 h-8 text-fg-quaternary" />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <button
              type="button"
              onClick={() => onOpenProfile(profileId, isCreator)}
              className="text-xl font-semibold text-primary underline decoration-dotted underline-offset-4 hover:decoration-solid text-left"
            >
              {creator.nickname ?? creator.username ?? ""}
            </button>
            {creator.isCreator && creator.collaborationCount != null && creator.collaborationCount > 0 && (
              <Tooltip
                title={`${creator.collaborationCount} previous collaboration${creator.collaborationCount !== 1 ? "s" : ""}`}
                description={creator.collaborationNames?.slice(0, 5).join(", ")}
              >
                <TooltipTrigger>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-success-50 border border-success-200 text-xs font-medium text-success-700 cursor-default">
                    <Target04 className="w-3.5 h-3.5" />
                    {creator.collaborationCount} collaboration{creator.collaborationCount !== 1 ? "s" : ""}
                  </span>
                </TooltipTrigger>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-tertiary">{categorySubtitle}</p>
        </div>
      </div>
      {description && <p className="text-sm text-secondary leading-relaxed">{description}</p>}
    </div>
  );
};

// ─── Brand creator profile ────────────────────────────────────────────────────

const BrandCreatorProfileInner = ({
  creator,
  onOpenProfile,
  onOpenVideo,
}: {
  creator: NormalizedSampleRequestCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
  onOpenVideo: (videoId: string) => void;
}) => {
  const currency = useCurrency();
  const { data, isLoading } = useCreator(creator.creatorId ?? undefined);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (isLoading) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!data) return <p className="p-8 text-sm text-tertiary">Creator profile not available.</p>;

  const topCategories = data.categories?.slice(0, 3) ?? [];
  const categorySubtitle = data.categories?.slice(0, 2).join(" | ") ?? creator.username ?? creator.nickname ?? "";

  const genderEntries = data.demographics?.gender
    ? (Object.entries(data.demographics.gender).filter(([, v]) => v != null) as [string, number][])
    : [];
  const topGender = genderEntries.reduce<[string, number] | null>(
    (best, curr) => (!best || curr[1] > best[1] ? curr : best),
    null,
  );

  const ageEntries = data.demographics?.ageDistribution
    ? (Object.entries(data.demographics.ageDistribution) as [string, number][])
    : [];
  const topAge = ageEntries.reduce<[string, number] | null>(
    (best, curr) => (!best || curr[1] > best[1] ? curr : best),
    null,
  );

  const videosForGrid = (data.videos ?? []).slice(0, 6).map((v) => ({
    id: v.videoId,
    thumbnail: v.urlFirstFrame,
    label1: "GMV",
    value1: currencyFormatter.format(v.gmv ?? 0),
    label2: "Views",
    value2: numFormatter.format(v.views ?? 0),
  }));

  return (
    <div className="flex gap-5">
      <div className="flex-1 min-w-0 p-8 flex flex-col gap-10">
        <ProfileHeader
          creator={creator}
          categorySubtitle={categorySubtitle}
          description={data.bioDescription}
          onOpenProfile={onOpenProfile}
        />
        {videosForGrid.length > 0 && <VideoGrid videos={videosForGrid} onVideoClick={onOpenVideo} />}
      </div>
      <ProfileRightColumn
        topCategories={topCategories}
        demographicsGender={topGender ? { label: topGender[0], value: percentFormatter.format(topGender[1]) } : null}
        demographicsAge={topAge ? { label: topAge[0], value: percentFormatter.format(topAge[1]) } : null}
        followersLabel={data.socialStats?.followers ? numFormatter.format(data.socialStats.followers) : "-"}
        avgCommissionRateLabel={
          data.averageCommissionRate != null ? `${Math.round(data.averageCommissionRate * 100)}%` : "-"
        }
        profilePage={data.profilePage}
        currencyFormatter={currencyFormatter}
        creatorClassification={data.classification}
        avgVideos={data.avgVideos}
        avgGMV={data.avgGMV}
        videosWithTier1={data.safetyAnalysis?.videosWithTier1}
        percentageTier2AtRisk={data.safetyAnalysis?.percentageTier2AtRisk}
      />
    </div>
  );
};

// ─── Candidate profile ────────────────────────────────────────────────────────

const CandidateProfileInner = ({
  creator,
  onOpenProfile,
}: {
  creator: NormalizedSampleRequestCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
}) => {
  const currency = useCurrency();
  const { data, isLoading } = useCandidate(creator.tiktokCreatorOpenId);

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (isLoading) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!data) return <p className="p-8 text-sm text-tertiary">Candidate profile not available.</p>;

  const topCategories =
    data.categoryGmvDistribution && data.categoryGmvDistribution.length > 0
      ? data.categoryGmvDistribution
          .filter((c) => c.category !== "-1")
          .slice(0, 3)
          .map((c) => c.category)
      : (data.categories?.slice(0, 3) ?? []);

  const categorySubtitle = data.categories?.slice(0, 2).join(" | ") ?? creator.username ?? creator.nickname ?? "";

  const topGender = data.followerGenderDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerGenderDistribution[0],
  );
  const topAgeRange = data.followerAgeDistribution?.reduce(
    (max, curr) => (Number.parseFloat(curr.percentage) > Number.parseFloat(max.percentage) ? curr : max),
    data.followerAgeDistribution[0],
  );

  const videosForGrid = (data.aiFeatures?.videos ?? []).slice(0, 6).map((v) => ({
    thumbnail: v.thumbnail,
    label1: "Views",
    value1: numFormatter.format(v.viewCount),
    label2: "Likes",
    value2: numFormatter.format(v.likeCount),
  }));

  return (
    <div className="flex gap-5">
      <div className="flex-1 min-w-0 p-8 flex flex-col gap-10">
        <ProfileHeader
          creator={creator}
          categorySubtitle={categorySubtitle}
          description={data.aiFeatures?.descriptiveSummaryOfCreator}
          onOpenProfile={onOpenProfile}
        />
        {videosForGrid.length > 0 && <VideoGrid videos={videosForGrid} />}
      </div>
      <ProfileRightColumn
        topCategories={topCategories}
        demographicsGender={
          topGender
            ? {
                label: topGender.gender,
                value: percentFormatter.format(Number.parseFloat(topGender.percentage)),
              }
            : null
        }
        demographicsAge={
          topAgeRange
            ? {
                label: topAgeRange.age,
                value: percentFormatter.format(Number.parseFloat(topAgeRange.percentage)),
              }
            : null
        }
        followersLabel={data.followerCount ? numFormatter.format(data.followerCount) : "-"}
        avgCommissionRateLabel={data.avgCommissionRate != null ? `${Math.round(data.avgCommissionRate * 100)}%` : "-"}
        profilePage={data.profilePage}
        candidateClassification={data.classification}
        ecVideoCount={data.ecVideoCount ?? 0}
        gmvAmount={data.gmvAmount ?? 0}
        currencyFormatter={currencyFormatter}
      />
    </div>
  );
};

// ─── Profile panel dispatcher ─────────────────────────────────────────────────

const SampleCreatorProfilePanel = ({
  creator,
  onOpenProfile,
  onOpenVideo,
}: {
  creator: NormalizedSampleRequestCreator;
  onOpenProfile: (id: string, isCreator?: boolean) => void;
  onOpenVideo: (videoId: string) => void;
}) => {
  // Use isCreator field from backend to determine if this is a brand creator or candidate
  // Brand creators exist in the creators table and have video/performance data
  // Candidates exist only in the candidates table (potential affiliates not yet working with merchant)
  return creator.isCreator ? (
    <BrandCreatorProfileInner creator={creator} onOpenProfile={onOpenProfile} onOpenVideo={onOpenVideo} />
  ) : (
    <CandidateProfileInner creator={creator} onOpenProfile={onOpenProfile} />
  );
};

// ─── Main view ────────────────────────────────────────────────────────────────

export const SampleRequestsView = ({ collaborationType = "target" }: { collaborationType?: "target" | "open" }) => {
  const { collaborationId } = useParams({ strict: false }) as { collaborationId: string };
  const navigate = useNavigate();
  const merchant = useMerchant();

  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  // When navigating backwards across a page boundary we want to land on the
  // last creator of the new page, not the first. This ref signals that intent.
  const selectLastOnPageLoadRef = useRef(false);
  const [checkedCreators, setCheckedCreators] = useState<Set<string>>(new Set());
  const [profileDrawer, setProfileDrawer] = useState<{ id: string; isCreator: boolean } | undefined>(undefined);
  const [videoDrawerId, setVideoDrawerId] = useState<string | undefined>(undefined);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [addToListsOpen, setAddToListsOpen] = useState(false);
  const [addToCandidateListOpen, setAddToCandidateListOpen] = useState(false);
  const [addBulkToListOpen, setAddBulkToListOpen] = useState(false);
  const [addBulkToCandidateListOpen, setAddBulkToCandidateListOpen] = useState(false);

  const isOpenCollab = collaborationType === "open";

  // Target collab data
  const { data: targetCollabDetail, isLoading: targetCollabLoading } = useTargetCollaborationDetail(
    isOpenCollab ? undefined : collaborationId,
  );

  // Open collab data
  const { data: openCollabDetail, isLoading: openCollabLoading } = useOpenCollaborationDetail(
    isOpenCollab ? collaborationId : undefined,
  );

  const collabLoading = isOpenCollab ? openCollabLoading : targetCollabLoading;
  // Normalized collaboration name for display
  const collaborationName = isOpenCollab ? openCollabDetail?.productTitle : targetCollabDetail?.name;

  // Push name sort to the server so ORDER BY applies before limit/offset.
  // Score (matchQualityScore) is only for target collab — not available for open.
  const serverSort = sortColumn === "name" ? ({ column: "nickname", direction: sortDirection } as const) : undefined;

  const {
    data: targetCreatorsData,
    isLoading: targetCreatorsLoading,
    isFetching: targetIsFetching,
  } = useTargetCollaborationCreators(isOpenCollab ? undefined : collaborationId, {
    status: "sample_requested",
    pagination: { limit: BATCH_SIZE, offset: page * BATCH_SIZE },
    sort: serverSort,
  });

  const {
    data: openCreatorsData,
    isLoading: openCreatorsLoading,
    isFetching: openIsFetching,
  } = useOpenCollaborationCreators(isOpenCollab ? collaborationId : undefined, {
    status: "sample_requested",
    pagination: { limit: BATCH_SIZE, offset: page * BATCH_SIZE },
    sort: serverSort,
  });

  const creatorsLoading = isOpenCollab ? openCreatorsLoading : targetCreatorsLoading;
  const isFetching = isOpenCollab ? openIsFetching : targetIsFetching;

  const { mutateAsync: approveSampleRequest, isPending: isApproving } = useApproveSampleRequest();
  const { mutateAsync: denySampleRequest, isPending: isDenying } = useDenySampleRequest();

  // Normalize creators to a unified shape
  const creatorsRaw: NormalizedSampleRequestCreator[] = useMemo(() => {
    if (isOpenCollab) {
      return (openCreatorsData?.creators ?? []).map((c) => ({
        tiktokCreatorOpenId: c.tiktokCreatorOpenId,
        creatorId: c.creatorId,
        isCreator: c.isCreator,
        nickname: c.nickname,
        avatar: c.avatar,
        status: c.status,
        avgGmvPerBuyerAmount: c.avgGmvPerBuyerAmount,
        avgGmvPerBuyerCurrency: c.avgGmvPerBuyerCurrency,
        followerCount: c.followerCount,
        sampleProducts: c.sampleProducts,
        pendingSampleApplicationId: c.pendingSampleApplicationId,
        matchQualityScore: null,
        collaborationCount: null,
        collaborationNames: null,
      }));
    }
    return (targetCreatorsData?.creators ?? []).map((c: TargetCollaborationCreator) => ({
      tiktokCreatorOpenId: c.tiktokCreatorOpenId,
      creatorId: c.creatorId,
      isCreator: c.isCreator,
      username: c.username,
      nickname: c.nickname,
      avatar: c.avatar,
      status: c.status,
      avgGmvPerBuyerAmount: c.avgGmvPerBuyerAmount,
      avgGmvPerBuyerCurrency: c.avgGmvPerBuyerCurrency,
      followerCount: c.followerCount,
      sampleProducts: c.sampleProducts,
      pendingSampleApplicationId: c.pendingSampleApplicationId,
      matchQualityScore: c.matchQualityScore,
      collaborationCount: c.collaborationCount,
      collaborationNames: c.collaborationNames,
    }));
  }, [isOpenCollab, openCreatorsData, targetCreatorsData]);

  const total = isOpenCollab ? (openCreatorsData?.total ?? 0) : (targetCreatorsData?.total ?? 0);

  // Client-side sort only needed for "score" (target collab only) — server already sorts for "name"
  const creators = useMemo(() => {
    if (sortColumn !== "score") return creatorsRaw;
    return [...creatorsRaw].sort((a, b) => {
      const cmp = (a.matchQualityScore ?? -1) - (b.matchQualityScore ?? -1);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [creatorsRaw, sortColumn, sortDirection]);

  const handleSortToggle = (col: SortColumn) => {
    setPage(0);
    if (sortColumn === col) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(col);
      setSortDirection("asc");
    }
  };

  const allChecked = creators.length > 0 && creators.every((c) => checkedCreators.has(c.tiktokCreatorOpenId));
  const someChecked = !allChecked && creators.some((c) => checkedCreators.has(c.tiktokCreatorOpenId));

  // Bulk selection breakdown — used to decide which "Add to List" modal to open
  const checkedArray = useMemo(
    () => creators.filter((c) => checkedCreators.has(c.tiktokCreatorOpenId)),
    [creators, checkedCreators],
  );
  const bulkAllCreators = checkedArray.length > 0 && checkedArray.every((c) => c.isCreator);
  const bulkAllCandidates = checkedArray.length > 0 && checkedArray.every((c) => !c.isCreator);

  const selectedIndex = useMemo(() => {
    if (!selectedCreatorId) return 0;
    const idx = creators.findIndex((c) => c.tiktokCreatorOpenId === selectedCreatorId);
    return idx >= 0 ? idx : 0;
  }, [selectedCreatorId, creators]);

  const selectedCreator = creators[selectedIndex] ?? null;
  const globalIndex = page * BATCH_SIZE + selectedIndex;

  useEffect(() => {
    // When navigating forward (next page) clear the selection so we start at
    // the top. The selectLastOnPageLoadRef overrides this for backward navigation.
    setSelectedCreatorId(null);
  }, [page]);

  // After creators load for a new page, check if we need to land on the last row
  // (set by handlePrevious when crossing a page boundary backwards).
  useEffect(() => {
    if (selectLastOnPageLoadRef.current && creators.length > 0) {
      selectLastOnPageLoadRef.current = false;
      setSelectedCreatorId(creators[creators.length - 1].tiktokCreatorOpenId);
    }
  }, [creators]);

  // Clamp page when total shrinks (e.g. after approve/deny removes rows)
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(total / BATCH_SIZE) - 1);
    setPage((p) => Math.min(p, maxPage));
  }, [total]);

  const canGoPrevious = globalIndex > 0;
  const canGoNext = globalIndex < total - 1;

  const handlePrevious = useCallback(() => {
    if (selectedIndex > 0) {
      setSelectedCreatorId(creators[selectedIndex - 1].tiktokCreatorOpenId);
    } else if (page > 0) {
      // Signal that when the previous page's creators load we should select
      // the last one, not the first (which is the default page-change behaviour).
      selectLastOnPageLoadRef.current = true;
      setPage((p) => p - 1);
    }
  }, [selectedIndex, creators, page]);

  const handleNext = useCallback(() => {
    if (selectedIndex < creators.length - 1) {
      setSelectedCreatorId(creators[selectedIndex + 1].tiktokCreatorOpenId);
    } else if ((page + 1) * BATCH_SIZE < total) {
      setPage((p) => p + 1);
    }
  }, [selectedIndex, creators, page, total]);

  const handleApprove = useCallback(async () => {
    if (!selectedCreator?.pendingSampleApplicationId) return;
    await approveSampleRequest({
      collaborationId,
      applicationId: selectedCreator.pendingSampleApplicationId,
    });
    success(`Approved sample request for ${selectedCreator.nickname ?? selectedCreator.username ?? "creator"}`);
    if (canGoNext) {
      handleNext();
    }
  }, [selectedCreator, approveSampleRequest, collaborationId, canGoNext, handleNext]);

  const handleDeny = useCallback(async () => {
    if (!selectedCreator?.pendingSampleApplicationId) return;
    await denySampleRequest({
      collaborationId,
      applicationId: selectedCreator.pendingSampleApplicationId,
    });
    success(`Denied sample request for ${selectedCreator.nickname ?? selectedCreator.username ?? "creator"}`);
    if (canGoNext) {
      handleNext();
    }
  }, [selectedCreator, denySampleRequest, collaborationId, canGoNext, handleNext]);

  const handleBulkApprove = useCallback(async () => {
    const targets = creators.filter((c) => checkedCreators.has(c.tiktokCreatorOpenId) && c.pendingSampleApplicationId);
    for (const c of targets) {
      await approveSampleRequest({ collaborationId, applicationId: c.pendingSampleApplicationId ?? "" });
    }
    setCheckedCreators(new Set());
    success(`Approved ${targets.length} sample request${targets.length !== 1 ? "s" : ""}`);
  }, [creators, checkedCreators, approveSampleRequest, collaborationId]);

  const handleBulkDeny = useCallback(async () => {
    const targets = creators.filter((c) => checkedCreators.has(c.tiktokCreatorOpenId) && c.pendingSampleApplicationId);
    for (const c of targets) {
      await denySampleRequest({ collaborationId, applicationId: c.pendingSampleApplicationId ?? "" });
    }
    setCheckedCreators(new Set());
    success(`Denied ${targets.length} sample request${targets.length !== 1 ? "s" : ""}`);
  }, [creators, checkedCreators, denySampleRequest, collaborationId]);

  const handleToggleAll = () => {
    if (allChecked) {
      setCheckedCreators(new Set());
    } else {
      setCheckedCreators(new Set(creators.map((c) => c.tiktokCreatorOpenId)));
    }
  };

  const handleToggleCreator = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedCreators((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBackNavigation = useCallback(() => {
    navigate({
      to: isOpenCollab
        ? "/merchant/$merchantId/collaborations/open/$collaborationId"
        : "/merchant/$merchantId/collaborations/target/$collaborationId",
      params: { merchantId: merchant, collaborationId },
    });
  }, [navigate, merchant, collaborationId, isOpenCollab]);

  const isLoading = collabLoading || creatorsLoading;
  const isActioning = isApproving || isDenying;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingIndicator size="lg" />
      </div>
    );
  }

  const collaboration = isOpenCollab ? openCollabDetail : targetCollabDetail;

  if (!collaboration) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <EmptyState size="md">
          <div className="pb-6">
            <EmptyState.FeaturedIcon color="gray" />
          </div>
          <EmptyState.Content>
            <EmptyState.Title>Collaboration not found</EmptyState.Title>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button
              color="secondary"
              size="md"
              onClick={() => navigate({ to: "/merchant/$merchantId/collaborations", params: { merchantId: merchant } })}
              iconLeading={ChevronLeft}
            >
              Back to Collaborations
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  if (total === 0 && !isFetching) {
    return (
      <div className="flex flex-col w-full h-full items-center">
        {/* Back button at top — outside the main content area */}
        <div className={cx("w-full px-15 pt-8", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
          <Button
            color="tertiary"
            onClick={handleBackNavigation}
            className="text-quaternary hover:text-primary pl-0 w-fit mb-3"
          >
            <div className="flex items-center">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to {collaborationName ?? "collaboration"}
            </div>
          </Button>
        </div>

        <div className={cx("flex w-full h-full px-16 pt-4 gap-6 pb-6", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
          <div className="w-[420px] flex flex-col flex-shrink-0">
            {/* Header */}
            <div className="pb-8 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <ShoppingBag01 className="w-5 h-5 text-fg-secondary flex-shrink-0" />
                <h1 className="text-xl font-normal text-primary">Review sample requests</h1>
              </div>
            </div>
            {/* Empty gray area */}
            <div className="flex-1 rounded-xl bg-bg-secondary" />
          </div>

          {/* Right panel — Empty state content */}
          <div className="flex-1 rounded-xl bg-bg-secondary flex flex-col items-center justify-center gap-4 min-w-[700px]">
            <div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
              <ShoppingBag01 className="w-6 h-6 text-fg-quaternary" />
            </div>
            <p className="text-sm font-semibold text-primary">All samples requests have been reviewed</p>
            <Button color="primary" size="md" iconLeading={ArrowLeft} onClick={handleBackNavigation}>
              Back to collaboration
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full items-center">
      {/* Back button at top — fixed height row */}
      <div className={cx("w-full px-15 pt-8 flex-shrink-0", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}>
        <Button
          color="tertiary"
          onClick={handleBackNavigation}
          className="text-quaternary hover:text-primary pl-0 w-fit"
        >
          <div className="flex items-center">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to {collaborationName ?? "collaboration"}
          </div>
        </Button>
      </div>

      <div
        className={cx("flex w-full flex-1 overflow-hidden px-16 pt-2 pb-6 gap-6", LAYOUT_CONSTRAINTS.CONTAINER_WIDTH)}
      >
        {/* Left panel — header + creator list */}
        <div className="w-[420px] flex flex-col flex-shrink-0 border-0 outline-none">
          {/* Header */}
          <div className="pb-8 flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <ShoppingBag01 className="w-5 h-5 text-fg-secondary flex-shrink-0" />
              <h1 className="text-xl font-normal text-primary">Review sample requests</h1>
            </div>
          </div>

          {/* Scrollable list */}
          <div className="flex-1 overflow-hidden relative">
            <div className="absolute inset-0 overflow-y-auto scrollbar-recs rounded-lg">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-11" />
                  <col />
                  <col className="w-[80px]" />
                  {!isOpenCollab && <col className="w-[60px]" />}
                </colgroup>

                <thead className="sticky top-0 z-10">
                  {checkedCreators.size > 0 ? (
                    /* Bulk-action header — replaces normal header when rows are selected */
                    <tr>
                      <th colSpan={4} className="p-0">
                        <div className="flex flex-row justify-between items-center bg-brand-primary px-3 py-2 border-b border-brand-300">
                          <div className="flex flex-row items-center gap-2">
                            <CloseButton onClick={() => setCheckedCreators(new Set())} />
                            <span className="text-sm">{checkedCreators.size} Selected</span>
                          </div>
                          <div className="flex flex-row gap-1 items-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button color="secondary" size="sm" iconLeading={DotsVertical} />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setShowMessageModal(true)}>
                                  <MessageTextSquare02 className="mr-2 size-4 text-fg-secondary" />
                                  Message Creator in TikTok
                                </DropdownMenuItem>
                                {(bulkAllCreators || bulkAllCandidates) && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      if (bulkAllCreators) setAddBulkToListOpen(true);
                                      else setAddBulkToCandidateListOpen(true);
                                    }}
                                  >
                                    <FilePlus02 className="mr-2 size-4 text-fg-secondary" />
                                    Add to List
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>

                            <Tooltip title="Deny request">
                              <Button
                                color="primary"
                                size="sm"
                                iconLeading={XClose}
                                onClick={handleBulkDeny}
                                isDisabled={isDenying}
                                className="*:data-icon:!text-white hover:*:data-icon:!text-white"
                              />
                            </Tooltip>

                            <Tooltip title="Approve sample request">
                              <Button
                                color="primary"
                                size="sm"
                                iconLeading={Check}
                                onClick={handleBulkApprove}
                                isDisabled={isApproving}
                                className="*:data-icon:!text-white hover:*:data-icon:!text-white"
                              />
                            </Tooltip>
                          </div>
                        </div>
                      </th>
                    </tr>
                  ) : (
                    /* Normal column headers */
                    <tr className="bg-[#FAFAFA] rounded-lg">
                      <th className="py-3 pl-4 pr-2 text-left align-middle border-b border-gray-200">
                        <button type="button" onClick={handleToggleAll} className="cursor-pointer">
                          <CheckboxBase isSelected={allChecked} isIndeterminate={someChecked} />
                        </button>
                      </th>
                      <th className="py-3 px-2 text-left align-middle border-b border-gray-200">
                        <button
                          type="button"
                          onClick={() => handleSortToggle("name")}
                          className="flex items-center gap-1.5 text-xs font-medium text-quaternary tracking-wide hover:text-fg-secondary transition-colors"
                        >
                          {total} Creator{total !== 1 ? "s" : ""}
                          <SortIcon active={sortColumn === "name"} direction={sortDirection} />
                        </button>
                      </th>
                      <th className="py-3 px-2 text-left align-middle border-b border-gray-200">
                        <span className="text-xs font-medium text-quaternary tracking-wide">Sample</span>
                      </th>
                      {!isOpenCollab && (
                        <th className="py-3 pl-2 pr-4 text-right align-middle border-b border-gray-200">
                          <button
                            type="button"
                            onClick={() => handleSortToggle("score")}
                            className="flex items-center gap-1.5 text-xs font-medium text-quaternary tracking-wide hover:text-fg-secondary transition-colors ml-auto"
                          >
                            Match
                            <SortIcon active={sortColumn === "score"} direction={sortDirection} />
                          </button>
                        </th>
                      )}
                    </tr>
                  )}
                </thead>

                <tbody>
                  {creators.map((creator) => {
                    const isChecked = checkedCreators.has(creator.tiktokCreatorOpenId);
                    const isSelected = creator.tiktokCreatorOpenId === selectedCreator?.tiktokCreatorOpenId;
                    return (
                      <tr
                        key={creator.tiktokCreatorOpenId}
                        className={cx(
                          "cursor-pointer transition-colors",
                          isChecked
                            ? "bg-brand-100 hover:bg-brand-50"
                            : isSelected
                              ? "bg-[#FAFAFA] hover:bg-gray-100"
                              : "bg-white hover:bg-gray-50",
                        )}
                        onClick={() => setSelectedCreatorId(creator.tiktokCreatorOpenId)}
                      >
                        <td
                          className="py-3 pl-4 pr-2 align-middle border-b border-gray-100"
                          onClick={(e) => handleToggleCreator(creator.tiktokCreatorOpenId, e)}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter")
                              handleToggleCreator(creator.tiktokCreatorOpenId, e as never);
                          }}
                        >
                          <CheckboxBase isSelected={isChecked} />
                        </td>
                        <td className="py-3 px-2 align-middle border-b border-gray-100 min-w-0">
                          <div className="flex items-center gap-2.5 overflow-hidden">
                            {creator.avatar ? (
                              <img
                                src={creator.avatar}
                                alt={creator.nickname ?? creator.username ?? ""}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-bg-quaternary flex items-center justify-center flex-shrink-0">
                                <User01 className="w-4 h-4 text-fg-quaternary" />
                              </div>
                            )}
                            <span className="text-sm font-medium text-primary truncate">
                              {creator.nickname ?? creator.username ?? ""}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-2 align-middle border-b border-gray-100">
                          <SampleProductThumbnails products={creator.sampleProducts} maxVisible={2} size="sm" />
                        </td>
                        {!isOpenCollab && (
                          <td className="py-3 pl-2 pr-4 align-middle border-b border-gray-100 text-right">
                            {creator.matchQualityScore != null ? (
                              <Tooltip
                                title="This creator's match score combines two factors"
                                description={
                                  <div className="flex flex-col gap-3 text-left">
                                    <div>
                                      <div className="font-medium mb-1">Revenue Potential</div>
                                      <ul className="list-disc list-inside text-xs">
                                        <li>
                                          Our AI analyses all creators and brand features (posting frequency, historical
                                          performance, audience demographics, etc.) to predict how much revenue they
                                          could generate for your brand.
                                        </li>
                                      </ul>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-1">Brand Fit</div>
                                      <ul className="list-disc list-inside text-xs">
                                        <li>
                                          We're comparing your brand vertical with the creators primary content category
                                          to identify alignment.
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                }
                              >
                                <TooltipTrigger>
                                  <span className="text-sm font-medium text-secondary cursor-help">
                                    {creator.matchQualityScore}%
                                  </span>
                                </TooltipTrigger>
                              </Tooltip>
                            ) : (
                              <span className="text-sm text-tertiary">-</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none z-20" />
          </div>
        </div>

        {/* Right panel — scrollable gray panel + sticky footer below */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-[700px]">
          {/* Gray rounded panel — scrollable */}
          <div className="flex-1 overflow-y-auto bg-bg-secondary rounded-xl">
            {selectedCreator ? (
              <SampleCreatorProfilePanel
                creator={selectedCreator}
                onOpenProfile={(id, isCreator) => setProfileDrawer({ id, isCreator: isCreator ?? false })}
                onOpenVideo={setVideoDrawerId}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-tertiary text-sm">
                Select a creator from the list
              </div>
            )}
          </div>

          {/* Bottom action bar — outside and below the gray panel */}
          <div
            className={cx(
              "py-4 flex-shrink-0 transition-opacity",
              checkedCreators.size > 0 && "opacity-40 pointer-events-none select-none",
            )}
          >
            <div className="flex items-center">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={!canGoPrevious || isActioning}
                className="w-10 h-10 flex items-center justify-center border border-border-secondary rounded-lg hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous"
              >
                <ArrowLeft className="w-5 h-5 text-fg-secondary" />
              </button>

              <span className="flex-1 text-sm text-secondary tabular-nums text-center">
                {globalIndex + 1} of {total}
              </span>

              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext || isActioning}
                className="w-10 h-10 flex items-center justify-center border border-border-secondary rounded-lg hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Next"
              >
                <ArrowRight className="w-5 h-5 text-fg-secondary" />
              </button>

              <div className="flex items-center gap-3 ml-3">
                {/* More actions dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center justify-center w-10 h-10 border border-border-secondary rounded-lg hover:bg-bg-secondary transition-colors"
                      aria-label="More actions"
                      disabled={!selectedCreator}
                    >
                      <DotsVertical className="w-5 h-5 text-fg-tertiary" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setShowMessageModal(true)}>
                      <MessageTextSquare02 className="mr-2 size-4 text-fg-secondary" />
                      Message Creator on TikTok
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        if (selectedCreator?.isCreator) {
                          setAddToListsOpen(true);
                        } else {
                          setAddToCandidateListOpen(true);
                        }
                      }}
                    >
                      <FilePlus02 className="mr-2 size-4 text-fg-secondary" />
                      Add to List
                    </DropdownMenuItem>
                    {selectedCreator?.isCreator && !isOpenCollab && (
                      <DropdownMenuItem onClick={() => setShowRemoveModal(true)}>
                        <XCircle className="mr-2 size-4 text-fg-secondary" />
                        Remove creator from collab
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  color="secondary"
                  size="md"
                  iconLeading={XClose}
                  onClick={handleDeny}
                  isDisabled={isActioning || !selectedCreator?.pendingSampleApplicationId}
                >
                  Deny request
                </Button>
                <Button
                  color="primary"
                  size="md"
                  iconLeading={Check}
                  onClick={handleApprove}
                  isDisabled={isActioning || !selectedCreator?.pendingSampleApplicationId}
                >
                  Approve Sample request
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Message modal — uses tiktokCreatorOpenId for both creators and candidates */}
        {selectedCreator && showMessageModal && (
          <MessageCreatorsDialog
            recipients={[
              {
                creatorId: selectedCreator.tiktokCreatorOpenId,
                handle: selectedCreator.nickname ?? selectedCreator.username ?? null,
                thumbnail: selectedCreator.avatar,
              },
            ]}
            open={showMessageModal}
            onOpenChange={(open) => {
              if (!open) setShowMessageModal(false);
            }}
          />
        )}

        {/* Remove from collab modal — brand creators only (target collab) */}
        {selectedCreator?.isCreator && selectedCreator.creatorId && showRemoveModal && !isOpenCollab && (
          <RemoveCreatorFromCollabModal
            creator={{
              creatorId: selectedCreator.creatorId,
              creatorName: selectedCreator.nickname ?? selectedCreator.username ?? "",
              thumbnail: selectedCreator.avatar,
              targetCollaborations: [
                {
                  collaborationId,
                  name: collaborationName ?? "",
                  status: selectedCreator.status,
                },
              ],
            }}
            open={showRemoveModal}
            onOpenChange={(open) => {
              if (!open) setShowRemoveModal(false);
            }}
            onSuccess={() => {
              setShowRemoveModal(false);
              if (canGoNext) handleNext();
            }}
          />
        )}

        {/* Single-selection: Add to creator list — brand creators only */}
        {selectedCreator?.isCreator && selectedCreator.creatorId && (
          <AddtoListsModal
            selectedCreators={[selectedCreator.creatorId]}
            onAddToList={() => setAddToListsOpen(false)}
            open={addToListsOpen}
            onOpenChange={setAddToListsOpen}
          />
        )}

        {/* Single-selection: Add to candidate list — candidates only */}
        {selectedCreator && !selectedCreator.isCreator && (
          <AddToCandidateListsModal
            selectedCandidates={[selectedCreator.tiktokCreatorOpenId]}
            onAddToList={() => setAddToCandidateListOpen(false)}
            open={addToCandidateListOpen}
            onOpenChange={setAddToCandidateListOpen}
          />
        )}

        {/* Bulk-selection: Add to creator list — shown only when all checked are brand creators */}
        {bulkAllCreators && (
          <AddtoListsModal
            selectedCreators={checkedArray.flatMap((c) => (c.creatorId ? [c.creatorId] : []))}
            onAddToList={() => setAddBulkToListOpen(false)}
            open={addBulkToListOpen}
            onOpenChange={setAddBulkToListOpen}
          />
        )}

        {/* Bulk-selection: Add to candidate list — shown only when all checked are candidates */}
        {bulkAllCandidates && (
          <AddToCandidateListsModal
            selectedCandidates={checkedArray.map((c) => c.tiktokCreatorOpenId)}
            onAddToList={() => setAddBulkToCandidateListOpen(false)}
            open={addBulkToCandidateListOpen}
            onOpenChange={setAddBulkToCandidateListOpen}
          />
        )}

        {/* Profile drawer */}
        {profileDrawer?.isCreator ? (
          <CreatorDetails id={profileDrawer.id} onClose={() => setProfileDrawer(undefined)} />
        ) : (
          <CandidateDetails id={profileDrawer?.id} onClose={() => setProfileDrawer(undefined)} />
        )}

        {/* Video drawer */}
        <VideoDetails id={videoDrawerId} onClose={() => setVideoDrawerId(undefined)} />
      </div>
    </div>
  );
};
